import { templateByKey } from '../marketing/marketingOptions'
import { MAX_HASHTAGS, offBrandWords, platformMeta } from './socialOptions'
import { fillTokens } from '../marketing/marketingRules'
import { today } from '../../core/format/dates'
import { isFeedable } from '../../utils/productFeed'
import type { Check } from '../editing'
import type { MarketingDesign } from '../../core/types/marketing'
import type { SocialPost } from '../../core/types/social'
import type { Product } from '../../core/types/catalog'

// Ook de footer van de winkel toont de handle; daarom staat de regel in core/.
export { instagramHandle } from '../../core/format/text'

export const emptyPost = (patch: Partial<SocialPost> = {}): SocialPost => ({
  id: '',
  title: '',
  date: today(),
  time: '19:30',
  platforms: ['Instagram-post'],
  pillar: 'kennis',
  status: 'concept',
  image: '',
  caption: '',
  hashtags: [],
  firstComment: '',
  link: '',
  consent: false,
  notes: '',
  ...patch,
})

/** Tekst zoals hij geplakt wordt: slimme woorden ingevuld, hashtags onderaan. */
export const postText = (p: SocialPost, tokens: Record<string, string>) => {
  const withTags = p.platforms.some((x) => platformMeta[x].hashtags)
  return [fillTokens(p.caption, tokens).trim(), withTags ? p.hashtags.join(' ') : ''].filter(Boolean).join('\n\n')
}

/** De strengste tekstlimiet van de gekozen kanalen. */
export const captionLimit = (p: SocialPost) => {
  const limits = p.platforms.filter((x) => platformMeta[x].caption).map((x) => ({ x, max: platformMeta[x].captionMax }))
  return limits.sort((a, b) => a.max - b.max)[0]
}

/** Instagram staat zoveel producttags per foto toe. */
export const MAX_PRODUCT_TAGS = 5

/** Wat moet er nog gebeuren voordat deze post online kan? */
export function postChecks(
  p: SocialPost,
  tokens: Record<string, string>,
  design?: MarketingDesign,
  product: (id: string) => Product | undefined = () => undefined,
): Check[] {
  const c: Check[] = []
  const has = (x: SocialPost['platforms'][number]) => p.platforms.includes(x)
  const text = postText(p, tokens)
  const needsCaption = p.platforms.some((x) => platformMeta[x].caption)

  if (!p.date) c.push({ level: 'error', text: 'Nog geen datum' })
  if (p.platforms.length === 0) c.push({ level: 'error', text: 'Kies waar je het plaatst' })
  if (!p.designId && !p.image) c.push({ level: 'error', text: 'Nog geen beeld: kies een ontwerp of upload een foto' })
  if (needsCaption && !p.caption.trim()) c.push({ level: 'error', text: 'Nog geen tekst bij het bericht' })

  const limit = captionLimit(p)
  if (limit && text.length > limit.max)
    c.push({ level: 'error', text: `Tekst is te lang voor ${limit.x} (${text.length} van max. ${limit.max} tekens)` })

  if (p.pillar === 'resultaat' && !p.consent)
    c.push({ level: 'error', text: 'Leg eerst vast dat de cliënt toestemming geeft voor haar foto of verhaal' })

  if (has('Instagram-post')) {
    if (p.hashtags.length > MAX_HASHTAGS)
      c.push({ level: 'error', text: `Instagram staat maximaal ${MAX_HASHTAGS} hashtags toe` })
    else if (p.hashtags.length > 10)
      c.push({ level: 'warning', text: '3 tot 8 gerichte hashtags werken beter dan veel algemene' })
    if (/https?:\/\/|www\./i.test(p.caption))
      c.push({ level: 'warning', text: 'Links in een Instagram-post zijn niet klikbaar. Schrijf “link in bio”.' })
    if (p.caption.trim() && p.caption.split('\n')[0].length > 125)
      c.push({ level: 'warning', text: 'De eerste zin is lang: alleen ± 125 tekens staan boven “meer”' })
  }
  const tags = p.productIds ?? []
  if (tags.length) {
    if (!has('Instagram-post')) c.push({ level: 'warning', text: 'Producten taggen kan alleen in een Instagram-post; de tags worden nu niet gebruikt' })
    if (tags.length > MAX_PRODUCT_TAGS) c.push({ level: 'error', text: `Instagram staat maximaal ${MAX_PRODUCT_TAGS} producttags per foto toe` })
    tags.forEach((id) => {
      const x = product(id)
      if (!x) c.push({ level: 'error', text: 'Een getagd product bestaat niet meer' })
      else if (!isFeedable(x)) c.push({ level: 'warning', text: `${x.name} staat niet in de Instagram-shop (geen foto of prijs), dus taggen lukt niet` })
    })
  }

  if (has('Instagram-story') && !p.link)
    c.push({ level: 'warning', text: 'Tip: voeg een link toe voor de linksticker, dan klikt ze direct door' })

  if (design) {
    const t = templateByKey(design.template)
    if (t.channel === 'Print') c.push({ level: 'warning', text: 'Dit is een printontwerp. Voor social past een post of story beter.' })
    else if (design.template === 'story' && has('Instagram-post'))
      c.push({ level: 'warning', text: 'Het ontwerp is een story (9:16). In een post wordt het bijgesneden.' })
    else if (design.template !== 'story' && has('Instagram-story'))
      c.push({ level: 'warning', text: 'Het ontwerp is een post (4:5). In een story komt er ruimte omheen.' })
  }

  const lower = p.caption.toLowerCase()
  offBrandWords
    .filter((w) => lower.includes(w))
    .forEach((w) => c.push({ level: 'warning', text: `“${w}” past niet bij hoe Beauty Bewust klinkt` }))

  if (p.date && p.date < today() && p.status !== 'geplaatst')
    c.push({ level: 'warning', text: 'Deze datum is voorbij. Al geplaatst? Zet de status op Geplaatst.' })

  return c
}
